import React, { useState, useEffect } from 'react';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AppLayout from './components/layout/AppLayout';
import HomePage from './components/game/HomePage';
import NadaContainer from './components/nada/NadaContainer';
import LevelGrid from './components/game/LevelGrid';
import GameLevel from './components/game/GameLevel';
import GameResults from './components/game/GameResults';
import ProgressDashboard from './components/progress/ProgressDashboard';
import CalibrationModal from './components/game/CalibrationModal';
import AchievementsPage from './components/achievements/AchievementsPage';
import OnboardingCarousel, { hasCompletedOnboarding } from './components/OnboardingCarousel';
import ErrorBoundary from './components/ErrorBoundary';
import { GameProvider, useGame } from './contexts/GameContext';
import { getAchievementById } from './utils/achievementsEngine';
import { hapticAchievement, hapticCalibrationSaved } from './utils/haptics';
import levelsData from './game/levels.json';

const theme = createTheme({
  palette: {
    primary: { main: '#58CC02' },
    secondary: { main: '#FF9600' },
    background: { default: '#f0f2f5', paper: '#ffffff' },
  },
  shape: { borderRadius: 12 },
  spacing: 8,
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
  },
  components: {
    // All Containers: consistent horizontal gutter on mobile
    MuiContainer: {
      defaultProps: { maxWidth: 'lg' },
      styleOverrides: {
        root: ({ theme }) => ({
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
          [theme.breakpoints.up('sm')]: {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
          },
        }),
      },
    },
    // All Papers: consistent border-radius and no flat edges
    MuiPaper: {
      defaultProps: { elevation: 2 },
      styleOverrides: {
        root: { borderRadius: 12 },
      },
    },
    // Cards share the same border-radius
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 12 },
      },
    },
    // Chips: pill shape
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
  },
});

function AppContent() {
  console.log('[BOOT] AppContent: mounting');
  const [currentView, setCurrentView] = useState('home');
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [gameResult, setGameResult] = useState(null);
  const [calibrationOpen, setCalibrationOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(!hasCompletedOnboarding());
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const { completeLevel, pendingAchievementToasts, dismissAchievementToast, progress, setJourneyPreferences, journeySequence } = useGame();

  // Orientation lock — portrait everywhere; gameLevel allows landscape for tracker
  useEffect(() => {
    const lockPortrait = async () => {
      try {
        if (currentView === 'gameLevel') {
          await ScreenOrientation.unlock();
        } else {
          await ScreenOrientation.lock({ orientation: 'portrait' });
        }
      } catch {
        // Non-Capacitor environment (web browser) — silently ignore
      }
    };
    lockPortrait();
  }, [currentView]);

  // Offline / online detection
  useEffect(() => {
    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => setIsOffline(false);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // Show one achievement toast at a time — grab the first queued one
  const activeToastId = pendingAchievementToasts[0] || null;
  const activeToastAchievement = activeToastId ? getAchievementById(activeToastId) : null;

  // Auto-dismiss after 4 s; also fire the distinctive achievement haptic pattern.
  useEffect(() => {
    if (!activeToastId) return;
    hapticAchievement();
    const t = setTimeout(() => dismissAchievementToast(activeToastId), 4000);
    return () => clearTimeout(t);
  }, [activeToastId, dismissAchievementToast]);

  const handleRequestCalibration = () => setCalibrationOpen(true);

  const handleCalibrated = (freq) => {
    // CalibrationModal already saved to localStorage and dispatched 'calibrationChanged'
    hapticCalibrationSaved();
    setCalibrationOpen(false);
  };

  const handleOnboardingComplete = (journeyTags = []) => {
    // Persist selected journey tags if any were chosen
    if (journeyTags && journeyTags.length > 0) {
      setJourneyPreferences(journeyTags);
    }
    setShowOnboarding(false);
    // If user has never calibrated, open calibration immediately after onboarding
    const hasCalibration = !!localStorage.getItem('userSaFrequency');
    if (!hasCalibration) {
      setCalibrationOpen(true);
    }
  };

  const handleShowTour = () => {
    // Clear the completion flag so the carousel shows from slide 1
    localStorage.removeItem('evcsOnboardingComplete');
    setShowOnboarding(true);
  };

  const handleNavigate = (view) => {
    import('./utils/audioEngine').then(({ playButtonTap }) => playButtonTap());
    setCurrentView(view);
  };

  const handleSelectLevel = (level) => {
    setSelectedLevel(level);
    setCurrentView('gameLevel');
  };

  const handleLevelComplete = (result) => {
    // Save to GameContext with full data
    completeLevel(
      result.levelId, 
      result.stars, 
      result.score,
      result.noteAccuracy,
      result.duration
    );
    setGameResult(result);
    setCurrentView('gameResults');
  };

  const handlePlayAgain = () => {
    setCurrentView('gameLevel');
  };

  const handleNextLevel = () => {
    const currentLevelId = selectedLevel.id;
    // If a journey sequence is active, advance along it; otherwise fall back to id+1
    if (journeySequence && journeySequence.length > 0) {
      const pos = journeySequence.findIndex((l) => l.id === currentLevelId);
      const nextLevel = pos >= 0 && pos < journeySequence.length - 1 ? journeySequence[pos + 1] : null;
      if (nextLevel) {
        setSelectedLevel(nextLevel);
        setCurrentView('gameLevel');
      } else {
        setCurrentView('levelGrid');
      }
    } else {
      const nextLevel = levelsData.levels.find((l) => l.id === currentLevelId + 1);
      if (nextLevel) {
        setSelectedLevel(nextLevel);
        setCurrentView('gameLevel');
      } else {
        setCurrentView('levelGrid');
      }
    }
  };

  const handleBackToLevelGrid = () => {
    setCurrentView('levelGrid');
  };

  const handleBackToHome = () => {
    setCurrentView('home');
  };

  const renderContent = () => {
    console.log('[BOOT] renderContent: view =', currentView);
    switch (currentView) {
      case 'home':
        return <HomePage onNavigate={handleNavigate} />;
      
      case 'nada':
        return <NadaContainer onBack={() => setCurrentView('home')} />;
      
      case 'play':
        // Redirect to level grid
        setCurrentView('levelGrid');
        return null;
      
      case 'levelGrid':
        return <LevelGrid onSelectLevel={handleSelectLevel} onChangeJourney={() => setShowOnboarding(true)} />;
      
      case 'gameLevel':
        return (
          <ErrorBoundary onReset={handleBackToLevelGrid}>
            <GameLevel
              level={selectedLevel}
              onComplete={handleLevelComplete}
              onBack={handleBackToLevelGrid}
              onRequestCalibration={handleRequestCalibration}
            />
          </ErrorBoundary>
        );
      
      case 'gameResults':
        const isLastLevel = selectedLevel.id === levelsData.levels[levelsData.levels.length - 1].id;
        return (
          <GameResults
            score={gameResult.score}
            stars={gameResult.stars}
            levelId={selectedLevel.id}
            levelName={selectedLevel.name}
            noteScores={gameResult.noteScores}
            onPlayAgain={handlePlayAgain}
            onNextLevel={handleNextLevel}
            onHome={handleBackToHome}
            isLastLevel={isLastLevel}
          />
        );
      
      case 'progress':
        return <ProgressDashboard onSelectLevel={handleSelectLevel} />;
      
      case 'achievements':
        return <AchievementsPage />;
      
      default:
        return <HomePage onNavigate={handleNavigate} />;
    }
  };

  return (
    <div className="App" onTouchStart={() => import('./utils/audioEngine').then(({ initAudio }) => initAudio())} onClick={() => import('./utils/audioEngine').then(({ initAudio }) => initAudio())}>
      <OnboardingCarousel
        open={showOnboarding}
        onComplete={handleOnboardingComplete}
        initialJourneyTags={progress.journeyPreferences || []}
      />
      <AppLayout currentView={currentView} onNavigate={handleNavigate} onRequestCalibration={handleRequestCalibration} onRequestTour={handleShowTour}>
        {renderContent()}
      </AppLayout>
      <CalibrationModal
        open={calibrationOpen}
        onClose={() => setCalibrationOpen(false)}
        onCalibrated={handleCalibrated}
      />

      {/* Offline warning */}
      <Snackbar
        open={isOffline}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" sx={{ fontWeight: 600 }}>
          📡 No internet connection — some features may not work
        </Alert>
      </Snackbar>

      {/* Achievement unlock toast — shows one at a time, auto-dismisses after 4s */}
      <Snackbar
        open={!!activeToastAchievement}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => activeToastId && dismissAchievementToast(activeToastId)}
      >
        <Alert
          onClose={() => activeToastId && dismissAchievementToast(activeToastId)}
          severity="success"
          variant="filled"
          icon={false}
          sx={{
            minWidth: 280,
            bgcolor: '#1a1a2e',
            color: '#fff',
            fontWeight: 600,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
            '& .MuiAlert-action': { color: '#aaa' },
          }}
        >
          {activeToastAchievement && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <span style={{ fontSize: '1.8rem', lineHeight: 1 }}>
                {activeToastAchievement.icon}
              </span>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', lineHeight: 1 }}>
                  🏆 Achievement Unlocked!
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
                  {activeToastAchievement.title}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                  {activeToastAchievement.description}
                </Typography>
              </Box>
            </Box>
          )}
        </Alert>
      </Snackbar>
    </div>
  );
}

function App() {
  console.log('[BOOT] App: rendering ThemeProvider+GameProvider');
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GameProvider>
        <AppContent />
      </GameProvider>
    </ThemeProvider>
  );
}

export default App;
